/**
 * Handles the known SSO errors the server can return after a failed authentication attempt.
 * It also allows other services/components to retrieve errors information based on their code.
 * @ngesdoc service
 * @ngname ssoErrors
 * @ngmodule auth
 */
class SSOErrors {
  /**
   * @param {ngLocation}            $location         To redirect the browser.
   * @param {FrontendConfiguration} appConfiguration  To read the error codes.
   * @param {AuthRoutesManager}     authRoutesManager To generate the route for the login screen.
   */
  constructor($location, appConfiguration, authRoutesManager) {
    'ngInject';

    /**
     * A local reference for the `$location` service.
     * @type {ngLocation}
     */
    this.$location = $location;
    /**
     * A local reference for the `authRoutesManager` service.
     * @type {AuthRoutesManager}
     */
    this.authRoutesManager = authRoutesManager;
    /**
     * The dictionary of known SSO errors the app can generate.
     * @type {GatewayConfigurationErrorCodesSettings}
     */
    this.errorCodes = appConfiguration.features.errorCodes;
  }
  /**
   * Gets the description of an error based on its code.
   * @param {string} code The error code.
   * @return {string} If there's no error with the recevied code, it will return an empty string.
   */
  getErrorDescription(code) {
    const ssoError = Object.keys(this.errorCodes)
    .find((name) => this.errorCodes[name].code === code);

    return ssoError ? this.errorCodes[ssoError].description : '';
  }
  /**
   * Redirects the browser with the error that says that the authentication route was loaded
   * without a valid callback.
   */
  invalidCallback() {
    this._handle(this.errorCodes.invalidCallback.code);
  }
  /**
   * Redirects the browser with the error that says that the user is not authorized to enter the
   * required implementation.
   */
  invalidPermissions() {
    this._handle(this.errorCodes.unauthorized.code);
  }
  /**
   * Redirects the browser with the error that says that the authentication route was loaded
   * without a valid callback and without being inside a popup window.
   */
  invalidPopup() {
    this._handle(this.errorCodes.invalidPopup.code);
  }
  /**
   * Redirects the browser with the error that says that the user doesn't have any customer
   * account assigned.
   */
  noAccounts() {
    this._handle(this.errorCodes.noAccounts.code);
  }
  /**
   * Redirects the browser with the error that says that there was an issue retrieving the user
   * information.
   */
  noUser() {
    this._handle(this.errorCodes.noUser.code);
  }
  /**
   * Redirects the browser to the login screen with an specified error code on the query string.
   * @param {string} code The error code.
   * @access protected
   * @ignore
   * @todo Check state
   */
  _handle(code) {
    this.$location.url(this.authRoutesManager.login(false, code));
  }
}

export default SSOErrors;
